import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'

import { NAV_LINKS, DEMO_HREF } from '@/constants/navigation'

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open])

    return (
        <header className='sticky top-0 z-50 px-3 pt-3 sm:px-4'>
            <div className='relative mx-auto max-w-[50rem]'>
                <nav
                    aria-label='Main'
                    className={cn(
                        'flex h-[60px] items-center gap-2 rounded-xl border pr-2 pl-4 backdrop-blur-md transition-[background-color,box-shadow,border-color] duration-500 ease-out',
                        scrolled
                            ? 'border-white/80 bg-[#f6f4ec]/95 shadow-[0_1px_2px_rgba(35,34,30,0.06),0_18px_40px_-18px_rgba(35,34,30,0.35)]'
                            : 'border-white/60 bg-[#f6f4ec]/85 shadow-[0_1px_2px_rgba(35,34,30,0.05),0_8px_24px_-12px_rgba(35,34,30,0.12)]'
                    )}
                >
                    <Link
                        href='/'
                        aria-label='Closewell home'
                        className='group rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    >
                        <Logo />
                    </Link>

                    <ul className='ml-auto hidden items-center gap-1 md:flex'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    className='relative rounded-md px-3.5 py-2 text-[15px] text-foreground/80 transition-colors after:absolute after:inset-x-3.5 after:bottom-1 after:h-px after:origin-left after:scale-x-0 after:bg-foreground/60 after:transition-transform after:duration-300 after:ease-out hover:text-foreground hover:after:scale-x-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <div className='ml-6 hidden items-center gap-2 md:flex'>
                        <ButtonLink href='/contact' variant='secondary' size='sm'>
                            Contact
                        </ButtonLink>

                        <ButtonLink href={DEMO_HREF} size='sm'>
                            Book a demo
                        </ButtonLink>
                    </div>

                    <button
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        onClick={() => setOpen(value => !value)}
                        className='ml-auto inline-flex size-11 items-center justify-center rounded-md text-foreground transition-colors hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}

                        <span className='sr-only'>
                            {open ? 'Close menu' : 'Open menu'}
                        </span>
                    </button>
                </nav>

                <div
                    id='mobile-menu'
                    hidden={!open}
                    className={cn(
                        'absolute inset-x-0 top-full mt-2 rounded-xl border border-white/60 bg-[#f6f4ec]/95 p-2 shadow-[0_16px_40px_-16px_rgba(35,34,30,0.3)] backdrop-blur-md md:hidden',
                        open && 'animate-in fade-in slide-in-from-top-2 duration-200'
                    )}
                >
                    <ul className='flex flex-col'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    className='flex h-12 items-center rounded-lg px-3 text-base text-foreground transition-colors hover:bg-black/[0.04]'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <div className='mt-2 grid grid-cols-2 gap-2 border-t border-border/70 pt-3'>
                        <ButtonLink href='/contact' variant='secondary' onClick={() => setOpen(false)}>
                            Contact
                        </ButtonLink>

                        <ButtonLink href={DEMO_HREF} onClick={() => setOpen(false)}>
                            Book a demo
                        </ButtonLink>
                    </div>
                </div>
            </div>
        </header>
    )
}
